import isEqual from 'lodash/isEqual';
import React from 'react';

import { VariantPicker } from '@candu-ai/craft/ui';
import { useDefaultVariants } from '@candu-ai/styleguide/application-variants';
import { VariantNode } from '@candu-ai/styleguide/types';
import { useMultiNodes, useSelection } from 'craft/hooks';
import { usePreparedVariants } from 'craft/hooks/usePreparedVariants';
import { PropToEdit } from 'craft/types';

type GetConditions = (props: Record<string, PropToEdit>, custom: any) => Record<string, string>;

type VariantToProps = (variant: VariantNode) => Record<string, any>;
type VariantToCustom = (variant: VariantNode & { tagName?: string }) => any;

interface VarianSelectorPublicProps {
  componentName: string;
  getConditions: GetConditions;
  variantToProps?: VariantToProps;
  variantToCustom?: VariantToCustom;
}

const VariantsSelector = ({
  componentName,
  getConditions,
  variantToProps,
  variantToCustom,
}: VarianSelectorPublicProps) => {
  const { data } = useDefaultVariants();
  const { variants: styleguideVariants } = data || {};
  const { selection } = useSelection();
  const {
    props = {},
    custom = {},
    actions: { setProps, setCustom },
  } = useMultiNodes(selection.all, (node) => ({
    custom: node?.data.custom?.custom ?? {},
  }));
  const { name } = getConditions(props, custom);

  const variants = usePreparedVariants(componentName, styleguideVariants);

  const currentVariant = variants.find((v) => isEqual(v.value, name));

  const visibleVariants = variants.filter((v) => !v.isHidden);

  const onChangeVariant = (variantName: string) => {
    const variant = variants.find((v) => v.value === variantName);
    if (!variant) {
      return;
    }
    if (variantToProps) {
      setProps(variantToProps({ name: variantName, conditions: variant.conditions }));
    }
    if (variantToCustom) {
      setCustom({
        custom: variantToCustom({
          name: variantName,
          conditions: variant.conditions,
        }),
      });
    }
  };

  return (
    <VariantPicker
      selectedVariant={currentVariant ? currentVariant.name : 'Mixed Styles'}
      onSelectVariant={onChangeVariant}
      items={visibleVariants}
      tooltipContent="Select Component Variant"
      mode="Dark"
    />
  );
};

export const createVariantsSelector = (publicProps: VarianSelectorPublicProps) => {
  return (props: any) => <VariantsSelector {...publicProps} {...props} />;
};
